import { type FormEvent, useState } from "react";
import { BrandMark } from "@/components/brand/BrandMark";
import { Button, Input } from "@/components/ui";
import { useAuth } from "@/lib/auth";

/**
 * The only unauthenticated screen in the console.
 *
 * Three steps, all of which are consequences of how the admin pool is set
 * up: a password, possibly a second factor, and — on a first sign-in — a
 * real password to replace the temporary one an operator was invited with.
 * There is deliberately no "create account" and no "forgot password": the
 * pool refuses self sign-up, and a reset is `aws cognito-idp
 * admin-set-user-password` by somebody who already has access.
 *
 * Visually it is the console's own palette on a plain ground rather than a
 * marketing page. It is the first thing an operator sees at 3am when
 * something is wrong, and it should look like the tool, not like a product.
 */
export function SignInScreen() {
  const { signIn, confirmTotp, completeNewPassword, error, step } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      if (step === "totp_required" || step === "totp_setup_required") {
        await confirmTotp(code);
      } else if (step === "password_reset_required") {
        await completeNewPassword(newPassword);
      } else {
        await signIn(email, password);
      }
    } catch {
      // The provider already put a readable message in `error`.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-pink px-5 py-12">
      <div
        aria-hidden="true"
        className="absolute -left-32 -top-28 h-[34rem] w-[34rem] rounded-full bg-brand-green"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-52 -right-28 h-[32rem] w-[32rem] rounded-full border-[88px] border-white/80"
      />
      <div className="relative grid w-full max-w-[840px] overflow-hidden rounded-[32px] bg-white shadow-float md:grid-cols-[0.9fr_1.1fr]">
        <div className="relative hidden min-h-[540px] overflow-hidden bg-brand-green p-8 md:flex md:flex-col md:justify-between">
          <div className="absolute -left-20 top-28 h-64 w-72 rounded-[54%_46%_62%_38%/46%_55%_45%_54%] bg-brand-pink" />
          <div className="absolute -bottom-16 -right-10 h-64 w-64 rounded-full border-[52px] border-white" />
          <p className="relative text-[10px] font-bold uppercase tracking-[0.18em] text-on-brand/65">
            Private workspace
          </p>
          <div className="relative">
            <BrandMark className="mb-5 h-32 w-32" />
            <h1 className="max-w-[8ch] text-[42px] font-bold leading-[0.95] tracking-[-0.055em] text-on-brand">
              Keep the good stuff moving.
            </h1>
          </div>
        </div>

        <div className="p-7 sm:p-10 md:p-12">
          <div className="mb-8 flex items-center gap-3">
            <BrandMark className="h-14 w-14 md:hidden" />
            <div>
              <p className="text-[18px] font-bold tracking-[-0.025em]">
                Find Me Matcha
              </p>
              <p className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.17em] text-muted">
                Operator console
              </p>
            </div>
          </div>

          <h2 className="text-[28px] font-bold tracking-[-0.04em]">
            Welcome back
          </h2>
          <p className="mb-7 mt-1 text-[12px] text-muted">
            Sign in to review the corpus and keep queues clear.
          </p>

          <form onSubmit={submit} className="flex flex-col gap-4">
            {step === "idle" ? (
              <>
                <label htmlFor="admin-email" className="flex flex-col gap-1">
                  <span className="eyebrow">Email</span>
                  <Input
                    id="admin-email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                    required
                  />
                </label>
                <label htmlFor="admin-password" className="flex flex-col gap-1">
                  <span className="eyebrow">Password</span>
                  <Input
                    id="admin-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11"
                    required
                  />
                </label>
              </>
            ) : step === "password_reset_required" ? (
              <label
                htmlFor="admin-new-password"
                className="flex flex-col gap-1"
              >
                <span className="eyebrow">Choose a password</span>
                <Input
                  id="admin-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-11"
                  minLength={14}
                  required
                />
                <span className="text-micro text-muted">
                  At least 14 characters, with an upper case letter, a number
                  and a symbol.
                </span>
              </label>
            ) : (
              <label htmlFor="admin-totp" className="flex flex-col gap-1">
                <span className="eyebrow">
                  {step === "totp_setup_required"
                    ? "Set up your authenticator"
                    : "Authenticator code"}
                </span>
                <Input
                  id="admin-totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="h-11 font-mono tracking-[0.3em]"
                  maxLength={6}
                  required
                />
              </label>
            )}

            {error ? (
              <p className="rounded-control border border-danger/25 bg-danger-soft px-2 py-1.5 text-micro text-danger">
                {error}
              </p>
            ) : null}

            <Button
              type="submit"
              variant="primary"
              disabled={busy}
              className="mt-1 h-11 rounded-[13px]"
            >
              {busy ? "Working…" : step === "idle" ? "Sign in" : "Continue"}
            </Button>
          </form>

          <p className="mt-7 text-micro text-muted">
            Accounts are created by an existing operator. There is no sign-up.
          </p>
        </div>
      </div>
    </div>
  );
}
